import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { Loading } from '@/shared/utils/vuex-loading'
import { Api } from '@/api/api'
import { EmptyMessageState, MessageState } from '@/business/message/store/message-state'
import { CLEAR_INBOX, CREATE_MESSAGE, FETCH_MESSAGES } from '@/business/message/store/types'
import { MessageType } from '@/business/message/interfaces/message-type'
import { Injector } from '@/config/dependency-injection'
import { AuthenticationService } from '@/authentication/authentication-service'
import { MessageBox } from '@/business/message/models/message-box'
import { MessageRequest, MessageResponse, OrganisationLiteResponse } from '@/api/generated'

const STORE_NAME = 'message'

@Module({
  name: STORE_NAME,
})
export class MessageModule extends VuexModule {
  public static readonly STORE_NAME: string = STORE_NAME

  public data: MessageState = EmptyMessageState

  @Mutation
  public [CREATE_MESSAGE](message: MessageResponse) {
    const account = Injector.get(AuthenticationService).authenticatedUser()
    const inbox = this.data.inbox
    const receiver = message.receiver
    const sender = message.sender

    if (receiver.id === account.organisation.id) {
      appendMessageToBox(inbox, sender, sender, message, 'in')
    } else if (sender.id === account.organisation.id) {
      appendMessageToBox(inbox, receiver, sender, message, 'out')
    }

    this.data.inbox.contacts = Object.values(this.data.inbox.contactMap)
  }

  @Mutation
  public [CLEAR_INBOX]() {
    this.data = EmptyMessageState
  }

  @Mutation
  public [FETCH_MESSAGES](messageState: MessageState) {
    this.data = messageState
  }

  @Loading(CREATE_MESSAGE)
  @Action({
    commit: CREATE_MESSAGE,
    rawError: true,
  })
  public createMessage(request: MessageRequest): Promise<MessageResponse> {
    return Api.Messages.createMessage({
      messageRequest: request,
    })
  }

  @Loading(FETCH_MESSAGES)
  @Action({
    commit: FETCH_MESSAGES,
    rawError: true,
  })
  public fetchMessages(messageType: MessageType = MessageType.ALL): Promise<MessageState> {
    return Api.Messages.getMessages({
      messageType,
      page: 1,
      pageSize: 1000,
    }).then(response => {
      const account = Injector.get(AuthenticationService).authenticatedUser()

      const inbox: MessageBox = {
        contacts: [],
        contactMap: {},
      }

      const outbox: MessageBox = {
        contacts: [],
        contactMap: {},
      }

      for (const message of response.items) {
        const receiver = message.receiver
        const sender = message.sender

        if (receiver.id === account.organisation.id) {
          appendMessageToBox(inbox, sender, sender, message, 'in')
        } else if (sender.id === account.organisation.id) {
          appendMessageToBox(inbox, receiver, sender, message, 'out')
        }
      }

      inbox.contacts = Object.values(inbox.contactMap)
      outbox.contacts = Object.values(outbox.contactMap)

      return {
        inbox,
        outbox,
      }
    })
  }
}

function appendMessageToBox(
  box: MessageBox,
  contact: OrganisationLiteResponse,
  sender: OrganisationLiteResponse,
  message: MessageResponse,
  type: 'in' | 'out'
) {
  if (box.contactMap[contact.id] === undefined) {
    box.contactMap[contact.id] = {
      id: contact.id,
      image: contact.image,
      lastContact: new Date(),
      lastMessage: '',
      messages: [],
      name: contact.name,
      numberOfMessages: 0,
    }
  }

  ++box.contactMap[contact.id].numberOfMessages
  box.contactMap[contact.id].lastContact = message.createdAt
  box.contactMap[contact.id].lastMessage = message.content
  box.contactMap[contact.id].messages.push({
    attachments: [],
    content: message.content,
    createdAt: message.createdAt,
    image: sender.image,
    type,
  })
}
